import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * AlertSystemDisclaimer Component
 * 
 * Critical disclaimer informing users that the alert notification system
 * is NOT a substitute for contacting emergency services or authorities directly.
 * 
 * Usage:
 * - Client dashboard when viewing/configuring alerts
 * - Emergency contact setup screens
 * - Any alert notification settings pages
 */
const AlertSystemDisclaimer = ({ variant = 'default', className = '' }) => {
  const baseStyles = "rounded-lg border p-4 mb-4";
  
  const variantStyles = {
    default: "bg-amber-50 border-amber-300 text-amber-900",
    compact: "bg-amber-50 border-amber-300 text-amber-900 text-sm p-3",
    critical: "bg-red-50 border-red-300 text-red-900",
    inline: "bg-amber-50 border-amber-300 text-amber-800 text-xs p-2"
  };

  const iconSizes = {
    default: "h-6 w-6",
    compact: "h-5 w-5",
    critical: "h-8 w-8",
    inline: "h-4 w-4"
  };

  return (
    <div className={`${baseStyles} ${variantStyles[variant]} ${className}`}>
      <div className="flex items-start gap-3">
        <AlertTriangle 
          className={`${iconSizes[variant]} flex-shrink-0 mt-0.5`}
          aria-hidden="true"
        />
        <div className="flex-1">
          <h4 className="font-bold mb-2">
            {variant === 'critical' ? '⚠️ EMERGENCY NOTICE' : 'Important Notice'}
          </h4>
          <div className="space-y-2">
            <p className="font-semibold">
              This notification system is NOT a substitute for contacting emergency services.
            </p>
            <p>
              <strong>If you are experiencing a mental health emergency,
              or are in immediate danger:</strong>
            </p>
            <ul className="list-disc ml-5 space-y-1">
              <li>
                <strong>Call 911</strong> immediately for life-threatening emergencies
              </li>
              <li>
                <strong>Call 988</strong> (Suicide & Crisis Lifeline) for mental health crises
              </li>
              <li>
                <strong>Text "HELLO" to 741741</strong> (Crisis Text Line) for text support
              </li>
              <li>
                Contact your treatment provider, therapist, or clinician directly
              </li>
              <li>
                Go to your nearest emergency room
              </li>
            </ul>
            <p className="mt-3 text-sm italic">
              The MHDF Momentum alert system is designed to support ongoing wellness monitoring 
              and provide supplemental notifications to designated contacts. It operates with delays 
              and is not monitored 24/7. <strong>Always contact authorities or emergency services 
              directly for immediate assistance.</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertSystemDisclaimer;
